import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

const TOKEN_DURATION = 10 * 60 * 1000; // 10 minutes
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const ACTIVITY_DELAY = 500; // Debounce delay for activity detection

/**
 * InvisibleTimer - A component that runs session timeout logic in the background
 * with no visual UI. It handles:
 * 1. Logging out after 10 minutes of inactivity
 * 2. Resetting the 10-minute timer on any user activity
 * 3. Pausing timeout during Stripe checkout process
 */
const InvisibleTimer = () => {
  const { logout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(TOKEN_DURATION);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const location = useLocation();
  
  // Check if we're in a payment flow based on URL or localStorage
  useEffect(() => {
    // Detect Stripe checkout or payment confirmation pages
    const isPaymentFlow = 
      location.pathname.includes('/billing') || 
      location.pathname.includes('/payment') ||
      location.pathname.includes('/checkout') ||
      location.pathname.includes('/success') ||
      location.pathname.includes('/cancel');
    
    // Check for Stripe session ID in localStorage or URL params
    const hasStripeSession = 
      localStorage.getItem('stripe_session_id') || 
      new URLSearchParams(location.search).get('session_id');
    
    // Pause the timer during payment flows
    if (isPaymentFlow || hasStripeSession) {
      console.log('[InvisibleTimer] Payment flow detected, pausing session timeout');
      setIsPaused(true);
      
      // Store a flag in localStorage that we're in payment flow
      localStorage.setItem('payment_flow_active', 'true');
      
      // Reset timer as a precaution
      setTimeRemaining(TOKEN_DURATION);
    } else {
      // Check if we just returned from payment flow
      const wasInPaymentFlow = localStorage.getItem('payment_flow_active') === 'true';
      
      if (wasInPaymentFlow) {
        console.log('[InvisibleTimer] Returned from payment flow, resetting timeout');
        localStorage.removeItem('payment_flow_active');
        setTimeRemaining(TOKEN_DURATION);
      }
      
      setIsPaused(false);
    }
  }, [location]);
  
  // Set up the countdown timer - runs every minute rather than every second for efficiency
  useEffect(() => {
    console.log('[InvisibleTimer] Starting session timeout monitoring');
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Set up a new interval that decrements timeRemaining every minute (more efficient)
    intervalRef.current = setInterval(() => {
      setTimeRemaining(prevTime => {
        // If paused or in payment flow, don't decrement
        if (isPaused) {
          return prevTime;
        }
        
        // Decrement by 1 minute instead of 1 second
        const newTime = Math.max(0, prevTime - 60000);
        
        // Log only when minutes change or when less than 2 minutes left
        const minutesLeft = Math.ceil(newTime / 60000);
        if (minutesLeft <= 2 || minutesLeft % 5 === 0) {
          console.log('[InvisibleTimer] Session timeout in:', minutesLeft, 'minutes');
        }
        
        // Auto-logout when timer reaches zero
        if (newTime === 0) {
          // Double check we're not in payment flow before logging out
          const inPaymentFlow = localStorage.getItem('payment_flow_active') === 'true';
          if (!inPaymentFlow) {
            console.log('[InvisibleTimer] Session timeout reached, logging out');
            logout();
          } else {
            console.log('[InvisibleTimer] Session expired but payment in progress, extending');
            return TOKEN_DURATION; // Extend session during payment
          }
        }
        
        return newTime;
      });
    }, 60000); // Check every minute instead of every second
    
    // Clean up the interval when component unmounts
    return () => {
      console.log('[InvisibleTimer] Cleaning up session monitoring');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [logout, isPaused]); // Add isPaused as dependency to react to changes
  
  // Track user activity and reset timer
  const updateActivity = useCallback(() => {
    // Don't update if we're paused
    if (isPaused) return;
    
    const now = Date.now();
    setLastActivity(now);
    
    // Clear any pending timeout to avoid multiple rapid resets
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    // Debounce the activity detection to avoid resetting timer on every tiny movement
    activityTimeoutRef.current = setTimeout(() => {
      // Only log when timer was significantly depleted (save console output)
      if (timeRemaining < TOKEN_DURATION - 60000) {
        console.log('[InvisibleTimer] User activity detected, resetting timer');
      }
      setTimeRemaining(TOKEN_DURATION);
    }, ACTIVITY_DELAY);
  }, [timeRemaining, isPaused]);
  
  // Set up event listeners for user activity (throttled)
  useEffect(() => {
    // Events to track user activity
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    
    // Throttled mousemove handler (only trigger once per second)
    let lastMoveTime = 0;
    const throttledMoveHandler = () => {
      const now = Date.now();
      if (now - lastMoveTime > 1000) {
        lastMoveTime = now;
        updateActivity();
      }
    };
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });
    
    // Add throttled mousemove handler separately
    document.addEventListener('mousemove', throttledMoveHandler);
    
    // Clean up event listeners
    return () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      
      document.removeEventListener('mousemove', throttledMoveHandler);
    };
  }, [updateActivity]);
  
  // Intercept and handle window storage events for cross-tab communication
  useEffect(() => {
    // Listen for Stripe session ID being set in another tab
    const handleStorageChange = (event) => {
      if (event.key === 'stripe_session_id' && event.newValue) {
        console.log('[InvisibleTimer] Stripe session detected in storage, pausing timer');
        setIsPaused(true);
        setTimeRemaining(TOKEN_DURATION);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.addEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Render nothing - this is a background component
  return null;
};

export default InvisibleTimer; 